import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PageHeader, StatusBadge, btnPrimary, colors } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { livenessFromLastSeen, relativeTime, type EnvironmentRow } from '../fleet/fleet-helpers'

interface DetailResponse {
  readonly environment: EnvironmentRow & { readonly ownerOrganizationName: string | null }
  readonly latestHeartbeat: {
    readonly id: string
    readonly receivedAt: string
    readonly version: string | null
    readonly syncLagSeconds: number | null
    readonly instrumentsActive: number | null
    readonly instrumentsTotal: number | null
  } | null
}

interface RotateResponse {
  readonly plaintextKey: string
  readonly displayedOnce: true
}

// Detail view shared by customer_admin and medflow_admin. Server owner
// scoping returns 404 for customer_admins looking at environments they
// don't own, so the UI doesn't need an additional role check here.
export function LicenseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<DetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rotatedKey, setRotatedKey] = useState<string | null>(null)
  const [rotating, setRotating] = useState(false)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    api
      .get<DetailResponse>(`/fleet/environments/${id}`)
      .then((res) => {
        if (cancelled) return
        setDetail(res.data ?? null)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load environment')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  async function rotateKey(): Promise<void> {
    if (!id) return
    if (!confirm('Rotate the enrollment key? The old key will stop working immediately.')) return
    setRotating(true)
    try {
      const res = await api.post<RotateResponse>(`/fleet/environments/${id}/rotate-key`, {})
      if (res.data) setRotatedKey(res.data.plaintextKey)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Rotate failed')
    } finally {
      setRotating(false)
    }
  }

  async function archive(): Promise<void> {
    if (!id) return
    if (!confirm('Archive this environment? Its heartbeats will stop appearing in the list.')) return
    try {
      await api.post(`/fleet/environments/${id}/archive`, {})
      navigate('/licenses')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Archive failed')
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>
  if (error) return <div style={{ color: colors.danger }}>{error}</div>
  if (!detail) return <div>Environment not found.</div>

  const env = detail.environment
  const liveness = livenessFromLastSeen(env.lastSeenAt)

  return (
    <div>
      <PageHeader
        title={env.name}
        subtitle={env.ownerOrganizationName ?? ''}
        backTo="/licenses"
        backLabel="Back to Environments"
        actions={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={rotateKey} disabled={rotating}>
              {rotating ? 'Rotating...' : 'Rotate key'}
            </button>
            <button type="button" onClick={archive}>
              Archive
            </button>
          </div>
        }
      />

      {rotatedKey && (
        <div
          style={{
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            color: '#92400e',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
          }}
          data-testid="rotated-key-banner"
        >
          <div style={{ marginBottom: '0.5rem' }}>
            <strong>New enrollment key.</strong> Shown exactly once. Paste it into the deployment and click Done.
          </div>
          <code style={{ display: 'block', wordBreak: 'break-all', padding: '0.5rem', background: '#fff' }}>
            {rotatedKey}
          </code>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1rem',
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '1.25rem',
          maxWidth: '900px',
        }}
      >
        <Field label="Liveness">
          <StatusBadge status={liveness} />
        </Field>
        <Field label="Environment type">{env.environmentType}</Field>
        <Field label="Version">{env.currentVersion ?? '—'}</Field>
        <Field label="Last seen">{relativeTime(env.lastSeenAt)}</Field>
        <Field label="Enrolled">{new Date(env.enrolledAt).toLocaleString()}</Field>
        <Field label="Latest heartbeat">
          {detail.latestHeartbeat ? (
            <>
              {relativeTime(detail.latestHeartbeat.receivedAt)} · {detail.latestHeartbeat.instrumentsActive ?? 0}/
              {detail.latestHeartbeat.instrumentsTotal ?? 0} instruments
            </>
          ) : (
            'none yet'
          )}
        </Field>
      </div>

      {env.notes && (
        <div style={{ marginTop: '1rem', color: colors.textMuted, whiteSpace: 'pre-wrap' }}>{env.notes}</div>
      )}

      <div style={{ marginTop: '1rem' }}>
        <button type="button" onClick={() => navigate('/licenses')} style={btnPrimary}>
          Back to list
        </button>
      </div>
    </div>
  )
}

function Field({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: '12px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: '15px', color: colors.textHeading, marginTop: '4px' }}>{children}</div>
    </div>
  )
}
